import { motion } from "framer-motion";
import { X } from "lucide-react";
import React, { useRef, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import BottomSheet from "./BottomSheet";
import { useIsMobile } from "../../hooks/responsive";

// Map of Tailwind max-width size values to their pixel equivalents
const maxWidthMap = {
  xs: "20rem", // 320px
  sm: "24rem", // 384px
  md: "28rem", // 448px
  lg: "32rem", // 512px
  xl: "36rem", // 576px
  "2xl": "42rem", // 672px
  "3xl": "48rem", // 768px
  "4xl": "56rem", // 896px
  "5xl": "64rem", // 1024px
  "6xl": "72rem", // 1152px
  "7xl": "80rem", // 1280px
  full: "100%",
  min: "min-content",
  max: "max-content",
  fit: "fit-content",
  prose: "65ch",
  // You can add more specific pixel values if needed
  "500px": "500px",
  "600px": "600px",
};

/**
 * Reusable Modal component with optimized animations
 * Automatically uses BottomSheet on mobile devices
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to display inside the modal
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {string} props.title - Modal title (optional)
 * @param {React.ReactNode} props.headerContent - Custom header content (optional)
 * @param {React.ReactNode} props.footerContent - Custom footer content (optional)
 * @param {string} props.maxWidth - Maximum width of the modal (default: "2xl")
 * @param {boolean} props.showCloseButton - Whether to show the close button (default: true)
 * @param {string} props.position - Position of the modal for desktop only (default: "center")
 * @param {number} props.zIndex - Custom z-index for the modal (default: 100)
 * @param {React.RefObject} props.customRef - Custom ref for the modal
 * @param {boolean} props.mobileFullscreen - Whether the modal should be fullscreen on mobile
 * @param {boolean} props.preventOutsideClick - Prevent closing when clicking outside
 * @param {string} props.headerPadding - Custom padding for the header (default: "tw-p-6")
 * @param {string} props.bodyPadding - Custom padding for the body (default: "tw-px-6 tw-py-4")
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.snapPoints - Array or function for bottom sheet snap points
 * @param {boolean} props.forceDesktopView - Force desktop modal view even on mobile devices
 * @param {string} props.maxHeight - Maximum height of the modal (default: "90vh")
 * @returns {JSX.Element}
 */
const Modal = ({
  children,
  onClose,
  title,
  headerContent,
  footerContent,
  maxWidth = "2xl",
  showCloseButton = true,
  position = "center",
  zIndex = 100,
  customRef,
  mobileFullscreen = false,
  preventOutsideClick = false,
  headerPadding = "tw-p-6",
  bodyPadding = "tw-px-6 tw-py-4",
  isOpen,
  snapPoints,
  forceDesktopView = false,
  maxHeight = "90vh",
}) => {
  const internalRef = useRef(null);
  const ref = customRef || internalRef;
  const [modalContent, setModalContent] = useState(null);

  // Detect if we're on a mobile device
  const isMobile = useIsMobile();

  // For controlled bottom sheet usage
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(isOpen);

  // Get the actual max width value from the mapping or use the value directly
  const actualMaxWidth = maxWidthMap[maxWidth] || maxWidth;

  // Update local state when isOpen prop changes
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsBottomSheetOpen(isOpen);
    }
  }, [isOpen]);

  // Prepare the appropriate modal content based on device type
  useEffect(() => {
    if (isMobile && !forceDesktopView) {
      // Use BottomSheet for mobile
      setModalContent(
        <BottomSheet
          open={isBottomSheetOpen !== undefined ? isBottomSheetOpen : true}
          onClose={onClose}
          title={title}
          headerContent={headerContent}
          footerContent={footerContent}
          showCloseButton={showCloseButton}
          zIndex={zIndex}
          customRef={ref}
          headerPadding={headerPadding}
          bodyPadding={bodyPadding}
          snapPoints={snapPoints}
        >
          {children}
        </BottomSheet>
      );
    } else {
      // Desktop view with regular modal
      setModalContent(
        createPortal(
          <div
            className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-p-4"
            style={{ zIndex: zIndex }}
          >
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="tw-fixed tw-inset-0 tw-bg-black/50 tw-will-change-transform"
              onClick={preventOutsideClick ? undefined : onClose}
              style={{ backdropFilter: "blur(5px)", zIndex }}
            />

            {/* Modal Content */}
            <motion.div
              ref={ref}
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, y: 20, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
                mass: 1,
              }}
              className="tw-bg-white tw-shadow-2xl tw-w-full tw-rounded-3xl tw-flex tw-flex-col tw-will-change-transform tw-overflow-hidden"
              style={{
                zIndex: zIndex + 1,
                maxWidth: actualMaxWidth,
                height: maxHeight,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header - Fixed height */}
              {(title || headerContent || showCloseButton) && (
                <div
                  className={`${headerPadding} tw-flex tw-justify-between tw-items-start tw-flex-shrink-0 tw-border-b tw-border-gray-100`}
                >
                  {title || headerContent ? (
                    <div className="tw-w-full">
                      {title && (
                        <h2 className="tw-text-2xl tw-font-bold tw-bg-gradient-to-r tw-from-gray-900 tw-to-gray-600 tw-bg-clip-text tw-text-transparent">
                          {title}
                        </h2>
                      )}
                      {headerContent}
                    </div>
                  ) : (
                    <div></div>
                  )}

                  {showCloseButton && (
                    <motion.button
                      whileHover={{ rotate: 90 }}
                      transition={{ duration: 0.2 }}
                      onClick={onClose}
                      className="tw-p-2 tw-rounded-full hover:tw-bg-gray-100"
                    >
                      <X className="tw-w-6 tw-h-6 tw-text-gray-500" />
                    </motion.button>
                  )}
                </div>
              )}

              {/* Body - Scrollable */}
              <div
                className={`${bodyPadding} tw-flex-1 tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-gray-300 tw-scrollbar-track-transparent`}
              >
                {children}
              </div>

              {/* Footer - Fixed height */}
              {footerContent && (
                <div className="tw-p-6 tw-border-t tw-border-gray-100 tw-flex-shrink-0">
                  {footerContent}
                </div>
              )}
            </motion.div>
          </div>,
          document.body
        )
      );
    }
  }, [
    isMobile,
    forceDesktopView,
    isBottomSheetOpen,
    onClose,
    title,
    headerContent,
    footerContent,
    showCloseButton,
    zIndex,
    ref,
    headerPadding,
    bodyPadding,
    snapPoints,
    children,
    preventOutsideClick,
    actualMaxWidth,
    maxHeight,
  ]);

  // Handle clicks outside of the modal for desktop view
  useEffect(() => {
    if (!isMobile || forceDesktopView) {
      function handleClickOutside(event) {
        if (
          ref.current &&
          !ref.current.contains(event.target) &&
          !preventOutsideClick
        ) {
          onClose();
        }
      }

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [onClose, ref, preventOutsideClick, isMobile, forceDesktopView]);

  return modalContent;
};

export default Modal;
